/* MapCase.js */

// JSON packets from server have snake_cased fields. Allow destructuring
/* eslint camelcase: [ "error", { properties: "never", allow: ["molecule_name", "gene_name"]}] */

import { ScopedPropertyGroup, fetchProperty, fetchPropertyGroup } from 'BMapsModel';
import { normalizePdbId } from '../util/pdb_id';

export class MapCase {
    constructor(obj) {
        /** @type {string} */
        this.molecule_name = obj.molecule_name;
        /** @type {string} */
        this.pdbID = obj.pdbID;
        this.description = obj.description;
        this.fragSpecs = obj.fragSpecs;
        /** @type {string} */
        this.uri = obj.uri; // May include mount index
        this.gene_name = obj.gene_name;
        this.project = obj.project;
        this.case = obj.case;
        /** @type {string} */
        this.projectCase = this.project && this.case ? `${this.project}/${this.case}`: ''; // Like uri, but without mount
        this.group_name = obj.group_name || obj.molecule_name;
        this.organism = obj.organism;
        this.sourceDesc = obj.sourceDesc; // Note about the origin
        this.lastUpdate = obj.lastUpdate || null;
        this.data = obj.data; // pdb data loaded by user
        this.data_format = obj.data_format; // format of user data (pdb or mol2)
        /** @type { import('BMapsModel').CaseDataCollection} */
        this.caseDataCollection = null;
        /** @type {DataSource} */
        this.dataSource = null;
        this.otherProperties = obj.otherProperties;
        this.scopedProperties = new ScopedPropertyGroup('target');
    }

    getDataSource() { return this.dataSource; }
    setDataSource(dataSource) { this.dataSource = dataSource; }
    getCaseDataCollection() { return this.caseDataCollection; }
    setCaseDataCollection(caseDataCollection) { this.caseDataCollection = caseDataCollection; }

    getProperty(scope, prop) { return this.scopedProperties.getPropertyValue(scope, prop); }
    getPropertyInfo(scope, prop) { return this.scopedProperties.getPropertyInfo(scope, prop); }
    setProperty(scope, prop, value) { this.scopedProperties.setPropertyValue(scope, prop, value); }
    getPropertyGroup(scope) { return this.scopedProperties.getScope(scope); }
    listProperties(listPropertiesParams) {
        return this.scopedProperties.listProperties(listPropertiesParams);
    }

    async fetchProperty(scope, prop, fn, trigger) {
        const propRef = this.scopedProperties.ensureProperty(scope, prop);
        return fetchProperty(propRef, fn, trigger);
    }

    async fetchPropertyGroup(scope, fn, trigger) {
        const groupRef = this.scopedProperties.ensureScope(scope);
        return fetchPropertyGroup(groupRef, fn, trigger);
    }

    static externalLinkInfo() { return null; }
    myExternalLinkInfo() {
        if (PdbImportCase.isValidId(this.pdbID)) {
            return PdbImportCase.externalLinkInfo(this.pdbID);
        }
        return MapCase.externalLinkInfo(this.pdbID);
    }

    hasDefaultMoleculeName() { return false; }

    /**
     * True for server / data-service cases (which can carry fragment data to load or align).
     * False for client-side imports (PDB / AlphaFold / user upload / folding), which never have
     * data-service fragments — get-available-fragments/load-fragments carry no case identity of
     * their own (BP2-2623), so sending them for one of these would misattribute another case's
     * data.
     */
    get isServerCase() {
        return !(this instanceof PdbImportCase
            || this instanceof AlphaFoldImportCase
            || this instanceof UserDataImportCase
            || this instanceof FoldingDataImportCase);
    }

    /** @returns {string} */
    get displayName() {
        return this.molecule_name
            || this.pdbID
            || this.projectCase
            || this.gene_name
            || this.description
            || '(unnamed target)';
    }

    get loadingMessage() { return 'Loading protein and fragment data...'; }

    getNodeName() {
        return (
            this.project
                ? this.projectCase
                : (this.pdbID || this.molecule_name || 'Unnamed structure')
        );
    }

    getLongName() {
        const protName = this.molecule_name || this.description || '';
        const pdbID = this.pdbID || '';
        const pdbDisplay = (protName && pdbID) ? ` (${pdbID})` : pdbID;
        return `${protName}${pdbDisplay}`;
    }

    getShortName() {
        return this.otherProperties?.shortName || this.pdbID || this.displayName;
    }

    static parseFragSpecs(spec) {
        //--- TBI
        return spec;
    }

    // Convert bfd-server maplist object to local class. Mostly passthrough (except pdbid)
    // Note: uri and lastUpdate are not part of the entries in the map-list.json file:
    // * uri is calculated here using the datasource mountIndex
    // * lastUpdate is added to the entries dynamically by bfd-server
    static fromBfdServer(map, dataSource) {
        const requiredFields = ['molecule_name', 'pdbid', 'description'];
        if (requiredFields.some((field) => map[field] == null)) {
            console.warn(`Ill-formatted map case obj: ${JSON.stringify(map)}`);
            return null;
        }

        let uri = `${map['project']}/${map['case']}`;
        if (dataSource?.mountIndex != null) {
            uri += ` mount${dataSource.mountIndex}`;
        }

        // lastUpdate from bfd server is a unix timestamp (seconds from epoch)
        // Multiply by 1000 since JS Date object expects ms from epoch
        const lastUpdate = map.lastUpdate != null ? new Date(map.lastUpdate * 1000) : null;
        if (map.sourceDesc === 'kinase-prep') map.sourceDesc = MapCase.Sources.Managed;
        const mapCase = new MapCase({
            molecule_name: map['molecule_name'],
            pdbID: map['pdbid'], // note case difference
            description: map['description'],
            fragSpecs: null, // NYI on server
            uri,
            gene_name: map['gene_name'],
            project: map['project'],
            case: map['case'],
            sourceDesc: map['sourceDesc'] || MapCase.Sources.Public,
            organism: map['organism'],
            group_name: map['group_name'],
            lastUpdate,
            otherProperties: map['otherProperties'],
        });
        mapCase.setDataSource(dataSource);
        return mapCase;
    }

    /**
     * Parse a project case string into project and case components
     * Test in MapCase.test.js
     * @param {string} projectCase
     * @returns {{ project: string?, case: string? }}
     */
    static splitProjectCase(projectCase) {
        const projectCasePattern = /^(?<project>[^\s/]+)\/(?<case>[^\s/]+)$/;
        const match = projectCase.match(projectCasePattern);
        return {
            project: match?.groups?.project || null,
            case: match?.groups?.case || null,
        };
    }

    /**
     * Parse a mapcase uri into projectCase and mountIndex components
     * Test in MapCase.test.js
     * @param {string} uri
     * @returns {{ projectCase: string?, mountIndex: string?, project: string?, case: string? }}
     */
    static splitUri(uri) {
        const ret = {
            projectCase: null,
            mountIndex: null,
            project: null,
            case: null,
        };

        const uriPattern = /^(?<projectCase>[^\s]+)( (?<mountIndex>mount\d+))?$/;
        const match = uri.match(uriPattern);
        if (!match) return ret;

        const { projectCase, mountIndex } = match.groups;
        ret.projectCase = projectCase;
        ret.mountIndex = mountIndex || null;

        const { project, case: caseName } = MapCase.splitProjectCase(projectCase);
        ret.project = project;
        ret.case = caseName;
        return ret;
    }
}

export class PdbImportCase extends MapCase {
    constructor(pdbID) {
        super({
            pdbID,
            group_name: MapCase.GroupNames.PdbImports,
            molecule_name: '',
            description: '',
            sourceDesc: MapCase.Sources.PDB,
            fragSpecs: '',
            uri: `pdb:${pdbID}`,
        });
    }

    get displayName() { return this.pdbID; }
    get loadingMessage() { return `Importing ${this.displayName} from the PDB`; }

    getNodeName() {
        return `PDB Import: ${this.pdbID}`;
    }

    // A number followed by 3 alphanumerics, with at least one letter (1abc), or an extended
    // id (pdb_00001abc). See util/pdb_id.js.
    static isValidId(id) {
        const normalized = normalizePdbId(id);
        return normalized !== '' && (normalized.startsWith('pdb_') || /[A-Za-z]/.test(normalized));
    }

    // The canonical form of a valid id: 1ABC for 1abc or pdb_00001abc, else pdb_xxxxxxxx.
    static normalizeId(id) {
        return normalizePdbId(id);
    }

    static isUri(uri) {
        const pattern = /^pdb:/;
        return pattern.test(uri);
    }

    static parseUri(uri) {
        return {
            id: uri.substr('pdb:'.length),
        };
    }

    static externalLinkInfo(id) {
        return {
            url: `https://www.rcsb.org/structure/${id}`,
            label: 'PDB',
            title: `Go to the original ${id} structure at the PDB`,
            faIcon: 'fa-external-link',
        };
    }

    myExternalLinkInfo() {
        return PdbImportCase.externalLinkInfo(this.pdbID);
    }

    static newRecordMsgInfo(id) {
        return {
            description: 'Use original structure from the protein data bank (no simulation data available)',
            itemName: `Import ${id}`,
        };
    }
}

export class AlphaFoldImportCase extends MapCase {
    constructor(uniprotID) {
        super({
            pdbID: uniprotID,
            group_name: MapCase.GroupNames.AlphaFoldImports,
            molecule_name: '',
            description: '',
            sourceDesc: MapCase.Sources.AlphaFold,
            fragSpecs: '',
            uri: `uniprot:${uniprotID}`,
        });
    }

    get displayName() { return this.pdbID; }
    get loadingMessage() { return `Importing ${this.displayName} from AlphaFold`; }

    getNodeName() {
        return `AlphaFold Import: ${this.pdbID}`;
    }

    static isValidId(id) {
        // From: https://www.uniprot.org/help/accession_numbers
        // An isoform adds -<n> (P04637-2); AlphaFold DB has models for isoforms too.
        const format = /^([OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2})(-[0-9]+)?$/;
        return format.test(id);
    }

    static isUri(uri) {
        const pattern = /^uniprot:/;
        return pattern.test(uri);
    }

    static parseUri(uri) {
        return {
            id: uri.substr('uniprot:'.length),
        };
    }

    static externalLinkInfo(id) {
        return {
            url: `https://alphafold.ebi.ac.uk/entry/${id}`,
            label: 'AlphaFold',
            title: `Go to UniProt:${id} structure at AlphaFold`,
            faIcon: 'fa-external-link',
        };
    }

    myExternalLinkInfo() {
        return AlphaFoldImportCase.externalLinkInfo(this.pdbID);
    }

    static newRecordMsgInfo(id) {
        return {
            description: 'Import AI generated structure from AlphaFold',
            itemName: `AlphaFold import UniProt:${id}`,
        };
    }
}

export class UserDataImportCase extends MapCase {
    static get DefaultRootMolName() { return 'Imported structure'; }

    constructor({
        name, pdbid, data, format, loadOptions,
    }) {
        super({
            group_name: MapCase.GroupNames.UserUploads,
            molecule_name: name || UserDataImportCase.newDefaultMolName(),
            pdbID: pdbid || '',
            description: '',
            sourceDesc: MapCase.Sources.ImportScreen,
            fragSpecs: '',
            uri: UserDataImportCase.newUri(),
            data,
            data_format: format,
        });
        this.loadOptions = loadOptions;
    }

    get loadingMessage() { return 'Importing protein data...'; }

    getNodeName() {
        return `User upload: ${this.pdbID || this.molecule_name}`;
    }

    getShortName() {
        let name = this.molecule_name;
        if (this.hasDefaultMoleculeName()) {
            const idNum = LocalIdManager.ExtractIdNum(this.uri, 'userdata');
            name = `Protein\u00A0${idNum}`; // \u00A0 = nonbreaking space
        }
        return this.pdbID || name;
    }

    hasDefaultMoleculeName() {
        return this.molecule_name === UserDataImportCase.DefaultRootMolName;
    }

    // stuff for user data /
    static newUri() {
        return LocalIdManager.GetNewUri('userdata');
    }

    static isUri(uri) {
        return LocalIdManager.IsUriFor(uri, 'userdata');
    }

    static newDefaultMolName() {
        return UserDataImportCase.DefaultRootMolName;
    }
}

export class FoldingDataImportCase extends MapCase {
    static get DefaultRootMolName() { return 'Folded structure'; }
    constructor({
        name, data, format,
        foldingData, targetId, loadOptions,
        resultProperties,
    }) {
        const uriKey = FoldingDataImportCase.uriKeyFor(targetId);
        const idNum = LocalIdManager.GetNewId(uriKey);
        const uri = LocalIdManager.FormUri(uriKey, idNum);
        super({
            group_name: MapCase.GroupNames.UserFolded,
            molecule_name: name || FoldingDataImportCase.newDefaultMolName(idNum, targetId),
            pdbID: '',
            description: `Folded with ${foldingData.foldingProgramId}`,
            fragSpecs: '',
            sourceDesc: `Folded in BMaps with ${foldingData.foldingProgramId}`,
            uri,
            data,
            data_format: format,
        });
        this.foldingData = foldingData;
        this.loadOptions = loadOptions;
        this.resultProperties = resultProperties;
        this.targetId = targetId;
        this.localIdNum = idNum;
    }

    get loadingMessage() { return 'Loading results of protein folding...'; }

    // targetId is only defined for screening job folds - decides which of the two
    // counter families this mints from, not what's embedded in the key: the key is
    // always one of these two fixed literals, never the target's own name. Keeping
    // targetId out of the key entirely (rather than a per-target key like
    // 'foldingTarget:BRAF') is deliberate - a per-target key bought nothing (the id
    // number is never displayed for a target-scoped fold - see getShortName), while
    // costing a real collision risk between two targets whose names happened to be
    // prefixes of each other. One shared counter for all target-scoped folds still
    // keeps them out of the standalone 'folding' counter, which is the only thing that
    // actually needed separating.
    static uriKeyFor(targetId) {
        return targetId ? 'foldingTarget' : 'folding';
    }

    // targetId is only ever set for a screening-derived fold (a standalone fold has no
    // "target" concept at all) - a sibling constructor param rather than nested inside
    // foldingData, so it isn't represented twice once promoted to this.targetId.
    // SavedState.CaptureProtein captures it explicitly alongside foldingData (see
    // there) for the same reason - it no longer rides along for free as part of that
    // object's own capture. Deliberately not a distinct class: that would bake "a
    // screening result is a kind of folding result" into the type hierarchy, which
    // stops being true the moment screening supports a non-folding method - this stays
    // pure data instead.
    //
    // Repeat loads of the same target are not disambiguated with a number (held off
    // deliberately, not an oversight): numbering them would need the number itself to
    // survive save/restore, and localIdNum is never captured - it's freshly re-minted
    // from the session's live, never-resetting counter on every construction, restore
    // included, so a restored session's new loads would show numbers well past what's
    // actually loaded (e.g. "CDK2 10" with only 3 structures present). Bare targetId
    // sidesteps that entirely - it's captured and restored explicitly in its own right
    // (see SavedState.CaptureProtein above) - at the cost of not telling repeat loads
    // of one target apart here.
    getShortName() {
        return this.targetId || `Folded ${this.localIdNum}`;
    }

    hasDefaultMoleculeName() {
        const pattern = new RegExp(`^${FoldingDataImportCase.DefaultRootMolName} \\d+$`);
        return pattern.test(this.molecule_name);
    }

    // Both counter families minted by uriKeyFor are fixed literals, so this is just an
    // exact match for each, OR'd together - same as every other class's isUri (compare
    // UserDataImportCase.isUri's single IsUriFor call).
    static isUri(uri) {
        return LocalIdManager.IsUriFor(uri, 'folding') || LocalIdManager.IsUriFor(uri, 'foldingTarget');
    }

    // Target case: bare targetId, matching getShortName() - see its comment for why
    // repeat loads aren't numbered. Standalone case: always numbered; unchanged,
    // longstanding behavior.
    static newDefaultMolName(idNum, targetId) {
        return targetId || `${FoldingDataImportCase.DefaultRootMolName} ${idNum}`;
    }

    myExternalLinkInfo() {
        const program = this.foldingData.foldingProgramId;
        return {
            label: program,
            title: `Download ${program} folding results`,
            faIcon: 'fa-download',
            download: {
                data: this.data,
                filename: `BMaps_${program}_results.${this.data_format}`,
            },
        };
    }
}

MapCase.GroupNames = {
    PdbImports: 'PDB Imports',
    UserUploads: 'User Uploads',
    AlphaFoldImports: 'AlphaFold Imports',
    UserFolded: 'Folded by User',
};
MapCase.Sources = {
    Public: 'bmaps_public', // Located in public bmaps repo
    Private: 'bmaps_private', // Located in a private, managed repo
    PDB: 'Imported from protein data bank', // Pulled from PDB
    ImportScreen: 'Import screen', // Added with Import tool
    Fragserv: 'fragserv', // Created with fragment service, and loaded from user repo
    AlphaFold: 'Imported from AlphaFold', // Pulled from AlphaFold
    Managed: 'bmaps_managed', // Company-managed curated dataset (e.g. the kinase selectivity library); routed like Public/cp, no bmaps_public/cp hardwiring
};

class LocalIdManager {
    static GetNewId(key) {
        if (!LocalIdManager.counters) LocalIdManager.counters = {};
        if (!LocalIdManager.counters[key]) LocalIdManager.counters[key] = 1;
        return LocalIdManager.counters[key]++;
    }

    static GetNewUri(key) {
        const id = LocalIdManager.GetNewId(key);
        return LocalIdManager.FormUri(key, id);
    }

    // The one place the "local:<key><id>" uri shape is defined, so a caller that
    // already has both pieces (e.g. from GetNewId) never needs to hand-format the
    // string itself, and GetNewUri above is just this plus minting the id. No
    // delimiter between key and id: every key today is a small, fixed literal
    // ('userdata', 'folding', 'foldingTarget'), and none is a prefix of another with a
    // digit immediately following the split point, so bare concatenation can't
    // collide. That stops being true the moment a key embeds caller-supplied content
    // (a per-target key did exactly that, and did collide - see uriKeyFor's comment) -
    // don't reintroduce one without also reintroducing a delimiter here.
    static FormUri(key, id) {
        return `local:${key}${id}`;
    }

    static IsUriFor(uri, key) {
        const pattern = LocalIdManager.patternFor(key);
        return pattern.test(uri);
    }

    // key is always a small, fixed literal today (see patternFor), so this is an exact
    // match: the digits right after key are unambiguously the id.
    static ExtractIdNum(uri, key) {
        const pattern = LocalIdManager.patternFor(key);
        const match = uri.match(pattern)?.groups.number;
        return match;
    }

    // key is always one of a small set of fixed literals today - UserDataImportCase's
    // 'userdata', and FoldingDataImportCase's 'folding'/'foldingTarget' (never the
    // dynamic targetId itself - see uriKeyFor's comment for why that stays out of the
    // key entirely). So this is a strict, exact match: key immediately followed by the
    // id digits and nothing else - see FormUri's comment for why that's safe only as
    // long as every key stays a fixed literal like these. Not escaped: none of these
    // literals contain a regex metacharacter, and there's no dynamic/externally-sourced
    // key reaching this anymore to worry about - reintroduce escaping if that changes.
    static patternFor(key) {
        return new RegExp(`^local:${key}(?<number>\\d+)$`);
    }
}
